import { useState, useEffect, useRef } from 'react';
import { depoimentos } from '../../mocks/depoimentos';
import FormularioDepoimento from './components/FormularioDepoimento';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Depoimentos | Histórias de Transformação — Método Bia Holmes',
  url: `${SITE_URL}/depoimentos`,
  description: 'Histórias reais de mulheres que transformaram carreira, saúde e relacionamentos com o Método Bia Holmes. Mais de 10.247 vidas transformadas com PNL, gratidão e reprogramação mental.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Depoimentos', item: `${SITE_URL}/depoimentos` },
    ],
  },
};

type Categoria = 'Todos' | 'Carreira' | 'Saúde' | 'Relacionamentos' | 'Autoestima' | 'Superação';

const categorias: { label: Categoria; icon: string; cor: string }[] = [
  { label: 'Todos', icon: 'ri-apps-2-line', cor: 'text-white/70' },
  { label: 'Carreira', icon: 'ri-briefcase-4-line', cor: 'text-amber-400' },
  { label: 'Saúde', icon: 'ri-heart-pulse-line', cor: 'text-rose-400' },
  { label: 'Relacionamentos', icon: 'ri-heart-3-line', cor: 'text-pink-400' },
  { label: 'Autoestima', icon: 'ri-user-smile-line', cor: 'text-violet-400' },
  { label: 'Superação', icon: 'ri-trophy-line', cor: 'text-gold-400' },
];

const destaquePrincipal = depoimentos.find(d => d.id === 9)!;

export default function DepoimentosPage() {
  const [filtro, setFiltro] = useState<Categoria>('Todos');
  const [ordenacao, setOrdenacao] = useState<'popularidade' | 'recentes'>('popularidade');
  const [expandidos, setExpandidos] = useState<number[]>([]);

  const toggleExpandir = (id: number) => {
    setExpandidos(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const lista = depoimentos
    .filter(d => filtro === 'Todos' || d.categoria === filtro)
    .sort((a, b) =>
      ordenacao === 'popularidade'
        ? b.curtidas - a.curtidas
        : new Date(b.dataPublicacao).getTime() - new Date(a.dataPublicacao).getTime()
    );

  return (
    <div className="min-h-screen bg-[#080808]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <SEOHead
        title="Depoimentos | Transformações em Carreira, Saúde e Relacionamentos — Método Bia Holmes"
        description="Histórias reais de mulheres que transformaram carreira, saúde e relacionamentos com o Método Bia Holmes. Mais de 10.247 vidas transformadas com PNL, gratidão e reprogramação mental."
        keywords="depoimentos bia holmes, transformação carreira, saúde mental PNL, relacionamentos saudáveis, reprogramação mental resultados"
        canonical="/depoimentos"
        ogImage="https://public.readdy.ai/ai/img_res/48398a990587f09cc1909c8c5456f1f2.jpg"
        ogType="website"
        schema={schema}
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/90 backdrop-blur-md border-b border-gold-500/15">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-gold-400 to-gold-700 rounded-lg">
              <i className="ri-quill-pen-fill text-[#0a0a0a] text-sm" />
            </div>
            <span className="text-white font-bold text-base tracking-tight">Carta do Eu Futuro</span>
          </a>
          <div className="hidden md:flex items-center gap-7">
            {[
              { href: '/', label: 'Início' },
              { href: '/sobre', label: 'Sobre a Bia' },
              { href: '/planos', label: 'Planos' },
              { href: '/depoimentos', label: 'Depoimentos', ativo: true },
            ].map(l => (
              <a
                key={l.href}
                href={l.href}
                className={`text-sm transition-colors ${l.ativo ? 'text-gold-400 font-semibold' : 'text-white/50 hover:text-white/90'}`}
              >
                {l.label}
              </a>
            ))}
          </div>
          <a
            href="/planos"
            className="px-5 py-2.5 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] rounded-full font-bold text-sm hover:from-gold-400 hover:to-gold-600 transition-all whitespace-nowrap"
          >
            Começar agora
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(201,164,64,0.12),transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-gold-500/40" />
              <span className="text-gold-400 text-xs font-semibold tracking-[0.3em] uppercase">Prova Social Real</span>
              <div className="h-px w-8 bg-gold-500/40" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'Georgia, serif' }}>
              Histórias que<br />
              <span className="text-gold-400">provam que funciona</span>
            </h1>
            <p className="text-white/45 text-lg leading-relaxed">
              Mulheres reais com resultados reais em carreira, saúde e relacionamentos. Sem milagre — apenas reprogramação mental com método.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { n: '10.247', l: 'Vidas transformadas', i: 'ri-group-line' },
              { n: '95%', l: 'Satisfação total', i: 'ri-heart-fill' },
              { n: '4.9/5', l: 'Avaliação média', i: 'ri-star-fill' },
              { n: '8.5k', l: 'Vitórias registradas', i: 'ri-trophy-line' },
            ].map((s, i) => (
              <div key={i} className="bg-white/3 border border-gold-500/12 rounded-xl p-5 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${s.i} text-gold-400 text-lg`} />
                </div>
                <div className="text-2xl font-bold text-gold-400 mb-1" style={{ fontFamily: 'Georgia, serif' }}>{s.n}</div>
                <div className="text-white/35 text-xs">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Depoimento Destaque */}
      <section className="pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-white/25 text-xs font-semibold tracking-widest uppercase mb-5">História em destaque</p>
          <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-gold-500/15">
            <div className="relative h-64 md:h-auto">
              <img
                src={destaquePrincipal.foto}
                alt={destaquePrincipal.nome}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0d0d0d]/60 md:block hidden" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] to-transparent md:hidden" />
              <div className="absolute top-4 left-4 bg-gold-500 text-[#0a0a0a] text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest">
                {destaquePrincipal.categoria}
              </div>
            </div>
            <div className="bg-[#0d0d0d] p-8 md:p-10 flex flex-col justify-center">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold-400 text-sm" />
                ))}
              </div>
              <blockquote className="text-white text-xl md:text-2xl font-medium leading-relaxed mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                &ldquo;{destaquePrincipal.fraseDestaque}&rdquo;
              </blockquote>
              <p className="text-white/45 text-sm leading-relaxed mb-6">{destaquePrincipal.depoimento}</p>
              <div>
                <p className="text-white font-semibold">{destaquePrincipal.nome}</p>
                <p className="text-gold-400/70 text-sm">{destaquePrincipal.cidade} · {destaquePrincipal.tempoUsando} no método</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {categorias.map(cat => (
                <button
                  key={cat.label}
                  onClick={() => setFiltro(cat.label)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    filtro === cat.label
                      ? 'bg-gold-500/15 border-gold-500/40 text-gold-400'
                      : 'bg-white/3 border-white/8 text-white/50 hover:border-white/20 hover:text-white/80'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} ${filtro === cat.label ? cat.cor : ''} text-sm`} />
                  </div>
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white/3 border border-white/8 rounded-full p-1 flex-shrink-0">
              <button
                onClick={() => setOrdenacao('popularidade')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  ordenacao === 'popularidade' ? 'bg-gold-500/20 text-gold-400' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Mais populares
              </button>
              <button
                onClick={() => setOrdenacao('recentes')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  ordenacao === 'recentes' ? 'bg-gold-500/20 text-gold-400' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Mais recentes
              </button>
            </div>
          </div>
          <p className="text-white/25 text-xs">
            {lista.length} {lista.length === 1 ? 'depoimento encontrado' : 'depoimentos encontrados'}
            {filtro !== 'Todos' ? ` em ${filtro}` : ''}
          </p>
        </div>
      </section>

      {/* Grid de Depoimentos */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {lista.map((dep, idx) => (
              <CardDepoimento
                key={dep.id}
                dep={dep}
                idx={idx}
                expandido={expandidos.includes(dep.id)}
                onToggle={() => toggleExpandir(dep.id)}
              />
            ))}
          </div>

          {lista.length === 0 && (
            <div className="text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-gold-400/40 text-4xl" />
              </div>
              <p className="text-white/30 text-sm">Nenhum depoimento encontrado nessa categoria ainda.</p>
            </div>
          )}
        </div>
      </section>

      {/* Categorias de resultado — visual blocks */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Resultados em todas as áreas
            </h2>
            <p className="text-white/40 text-sm max-w-xl mx-auto">
              O método Bia Holmes gera transformações profundas em cada dimensão da vida
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                titulo: 'Carreira & Negócios',
                icon: 'ri-briefcase-4-line',
                desc: 'Promoções, novos negócios, aumento de renda e liderança com confiança real.',
                stats: ['47 promoções registradas', 'R$ 2M+ em negócios criados', '89% de satisfação'],
                bg: 'from-amber-500/10 to-transparent',
                border: 'border-amber-500/20',
                cor: 'text-amber-400',
                img: 'https://readdy.ai/api/search-image?query=confident%20professional%20woman%20executive%20boardroom%20dark%20luxury%20background%20warm%20golden%20light%20career%20success%20achievement%20sophisticated%20premium%20photography&width=540&height=300&seq=res-carreira-v2&orientation=landscape',
              },
              {
                titulo: 'Saúde & Bem-estar',
                icon: 'ri-heart-pulse-line',
                desc: 'Superação de ansiedade, burnout, compulsões e retomada da energia vital.',
                stats: ['Burnout reduzido em 87%', 'Sono melhorado em 78%', '3.200+ recuperações'],
                bg: 'from-rose-500/10 to-transparent',
                border: 'border-rose-500/20',
                cor: 'text-rose-400',
                img: 'https://readdy.ai/api/search-image?query=woman%20meditating%20peaceful%20serene%20nature%20warm%20sunlight%20health%20wellness%20mindfulness%20calm%20golden%20hour%20beautiful%20authentic%20lifestyle%20photography&width=540&height=300&seq=res-saude-v2&orientation=landscape',
              },
              {
                titulo: 'Relacionamentos',
                icon: 'ri-heart-3-line',
                desc: 'Conexões mais profundas, reconciliações, autoamor e vínculos saudáveis.',
                stats: ['2.100+ conexões renovadas', '91% de autoestima elevada', 'Vínculos mais saudáveis'],
                bg: 'from-pink-500/10 to-transparent',
                border: 'border-pink-500/20',
                cor: 'text-pink-400',
                img: 'https://readdy.ai/api/search-image?query=two%20women%20friends%20laughing%20hugging%20warm%20genuine%20connection%20happiness%20authentic%20love%20beautiful%20sunlit%20outdoor%20lifestyle%20photography%20joyful&width=540&height=300&seq=res-relac-v2&orientation=landscape',
              },
            ].map((item, i) => (
              <div key={i} className={`bg-gradient-to-b ${item.bg} border ${item.border} rounded-2xl overflow-hidden`}>
                <div className="h-40 overflow-hidden">
                  <img src={item.img} alt={item.titulo} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} ${item.cor} text-base`} />
                    </div>
                    <h3 className="text-white font-bold text-base">{item.titulo}</h3>
                  </div>
                  <p className="text-white/40 text-sm leading-relaxed mb-4">{item.desc}</p>
                  <div className="space-y-1.5">
                    {item.stats.map((s, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <i className="ri-check-line text-gold-400 text-xs" />
                        <span className="text-white/55 text-xs">{s}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setFiltro(
                      item.titulo.startsWith('Carreira') ? 'Carreira'
                        : item.titulo.startsWith('Saúde') ? 'Saúde'
                        : 'Relacionamentos'
                    )}
                    className={`mt-5 w-full py-2.5 rounded-lg border ${item.border} ${item.cor} text-xs font-semibold hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap`}
                  >
                    Ver depoimentos de {item.titulo.split(' &')[0]}
                    <i className="ri-arrow-right-line ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulário + CTA */}
      <FormularioDepoimento />

      {/* CTA Final */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-b from-[#1a1400] to-[#0f0c00] border border-gold-500/20 rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(201,164,64,0.12),transparent_60%)]" />
            <div className="relative">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6 bg-gold-500/15 border border-gold-500/30 rounded-2xl">
                <i className="ri-quill-pen-line text-gold-400 text-2xl" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                Sua história pode ser a próxima
              </h2>
              <p className="text-white/45 text-base mb-8 leading-relaxed">
                Mais de 10.000 mulheres já transformaram suas vidas. Comece hoje mesmo a reprogramar seus padrões mentais.
              </p>
              <a
                href="/planos"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] rounded-full font-bold hover:from-gold-400 hover:to-gold-600 transition-all whitespace-nowrap text-sm"
              >
                Começar minha jornada
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer minimalista */}
      <footer className="border-t border-gold-500/10 py-10 px-6 bg-[#060606]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-gold-400 to-gold-700 rounded-lg">
              <i className="ri-quill-pen-fill text-[#0a0a0a] text-xs" />
            </div>
            <span className="text-white/70 font-semibold text-sm">Carta do Eu Futuro</span>
          </a>
          <div className="flex gap-6">
            {[
              { href: '/', l: 'Início' },
              { href: '/planos', l: 'Planos' },
              { href: '/faq', l: 'FAQ' },
              { href: '/privacidade', l: 'Privacidade' },
            ].map(link => (
              <a key={link.href} href={link.href} className="text-white/30 hover:text-white/70 text-xs transition-colors">
                {link.l}
              </a>
            ))}
          </div>
          <p className="text-white/20 text-xs">© 2024 Carta do Eu Futuro</p>
        </div>
      </footer>
    </div>
  );
}

function CardDepoimento({
  dep, idx, expandido, onToggle,
}: {
  dep: typeof depoimentos[0];
  idx: number;
  expandido: boolean;
  onToggle: () => void;
}) {
  const [visivel, setVisivel] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => setVisivel(true), idx * 60);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, [idx]);

  const catCor: Record<string, string> = {
    Carreira: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    Saúde: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
    Relacionamentos: 'text-pink-400 bg-pink-500/10 border-pink-500/20',
    Autoestima: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
    Superação: 'text-gold-400 bg-gold-500/10 border-gold-500/20',
  };

  const longo = dep.depoimento.length > 260;
  const texto = expandido || !longo ? dep.depoimento : dep.depoimento.slice(0, 260) + '...';

  return (
    <div
      ref={ref}
      className={`bg-[#0e0e0e] border border-white/6 rounded-2xl p-6 hover:border-gold-500/20 transition-all duration-700 flex flex-col ${
        visivel ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full overflow-hidden border border-gold-500/20 flex-shrink-0">
          <img src={dep.foto} alt={dep.nome} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm leading-tight">{dep.nome}</p>
          <p className="text-white/35 text-xs mt-0.5">{dep.cidade}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <i key={i} className="ri-star-fill text-gold-400 text-[10px]" />
              ))}
            </div>
            <span className="text-white/25 text-[10px]">· {dep.tempoUsando}</span>
          </div>
        </div>
        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border flex-shrink-0 ${catCor[dep.categoria] || 'text-white/50 bg-white/5 border-white/10'}`}>
          {dep.categoria}
        </span>
      </div>

      {/* Frase destaque */}
      <div className="border-l-2 border-gold-500/30 pl-3 mb-4">
        <p className="text-gold-400/80 text-sm italic leading-relaxed">&ldquo;{dep.fraseDestaque}&rdquo;</p>
      </div>

      {/* Depoimento */}
      <p className="text-white/45 text-xs leading-relaxed flex-1">{texto}</p>

      {longo && (
        <button
          onClick={onToggle}
          className="mt-3 text-gold-400/70 hover:text-gold-400 text-xs font-medium flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
        >
          {expandido ? 'Ler menos' : 'Ler mais'}
          <i className={`${expandido ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
        </button>
      )}

      {/* Footer do card */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/5">
        <div className="flex items-center gap-1.5 text-white/30 text-xs">
          <i className="ri-heart-fill text-gold-400/50 text-xs" />
          <span>{dep.curtidas}</span>
        </div>
        <span className="text-white/20 text-[10px]">{dep.tipoTransformacao}</span>
      </div>
    </div>
  );
}
